import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useOrgStore } from '@/stores/useOrgStore';

interface Quote {
  quote: string;
  type: string;
}

export function TopQuoteBar() {
  const { activeRole } = useOrgStore();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [index, setIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadQuotes = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-role-quotes', {
          body: { role: activeRole || 'general' },
        });

        if (error) {
          console.error('Error loading quotes:', error);
          setQuotes([{ quote: 'Excellence is a habit, not an event.', type: 'motivation' }]);
          return;
        }

        const quotesList = data as Quote[];
        if (quotesList.length > 0) {
          setQuotes(quotesList);
          setIndex(Math.floor(Math.random() * quotesList.length));
        } else {
          setQuotes([{ quote: 'Excellence is a habit, not an event.', type: 'motivation' }]);
        }
      } catch (err) {
        console.error('Failed to load quotes:', err);
        setQuotes([{ quote: 'Excellence is a habit, not an event.', type: 'motivation' }]);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuotes();
  }, [activeRole]);

  useEffect(() => {
    if (!quotes.length) return;

    // Rotate every 30 minutes (1800000ms)
    const intervalId = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % quotes.length);
    }, 1800000);

    return () => clearInterval(intervalId);
  }, [quotes]);

  const currentQuote = quotes[index];
  
  if (isLoading || !currentQuote) {
    return null;
  }

  // Color based on type
  const getColor = (type: string) => {
    switch (type) {
      case 'technique':
        return 'text-secondary'; // Teal
      case 'knowledge':
        return 'text-blue-500';
      default:
        return 'text-primary'; // Gold
    }
  };

  return (
    <div className="max-w-2xl flex items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.p
          key={index}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className={`text-sm md:text-base font-medium tracking-wide text-center line-clamp-1 ${getColor(currentQuote.type)}`}
        >
          "{currentQuote.quote}"
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
